// imports
import { Box, Button } from '@mui/material';
import { useStripe } from '@stripe/react-stripe-js';
import { FC } from 'react';

// components
import { Alert } from 'components/common/Alert';
import { LoadingButton } from 'components/common/LoadingButton';

// constants, helpers
import {
  ADD_BANK_ACCOUNT_TEXT,
  CANCEL_TEXT,
  STRIPE_NOT_LOADED_TEXT,
  ACCOUNT_ATTACH_SUCCESS_MSG,
} from 'constants/constant';
import { HTTP_STATUS } from 'constants/index';
import { useAuthContext } from 'contexts/AuthContext';
import { useCreateSetupIntentMutation } from 'generated/graphql';
import { flexCenterJustifyEnd } from 'theme/styleConstant';
import { AddBankAccountFormProps } from 'interfaces/PropsTypes';

const AddBankAccountForm: FC<AddBankAccountFormProps> = ({
  setFormLoading,
  formLoading,
  onComplete,
  onCancel,
}) => {
  const stripe = useStripe();
  const { currentUser } = useAuthContext();

  const [createSetupIntent] = useCreateSetupIntentMutation({
    onCompleted: async (data) => {
      const { createSetupIntent: result } = data;
      const { response, clientSecret } = result || {};
      const { status, message } = response || {};

      if (status !== HTTP_STATUS.SUCCESS || !clientSecret) {
        Alert.error(message ?? 'Failed to create setup session');
        setFormLoading(false);
        return;
      }

      if (!stripe) {
        Alert.error(STRIPE_NOT_LOADED_TEXT);
        setFormLoading(false);
        return;
      }

      const firstName = currentUser?.firstName ?? '';
      const lastName = currentUser?.lastName ?? '';
      const name = ([firstName, lastName].filter(Boolean).join(' ') || currentUser?.email) ?? '';

      const result_ = await stripe.collectBankAccountForSetup({
        clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: {
              name: name,
              email: currentUser?.email ?? '',
            },
          },
        },
      });

      setFormLoading(false);

      const { error, setupIntent } = result_;

      if (error) {
        Alert.error(error.message ?? 'Failed to add bank account');
        return;
      }

      const { status: setupIntentStatus } = setupIntent || {};

      switch (setupIntentStatus) {
        case 'canceled':
          break;

        case 'succeeded':
          Alert.success(ACCOUNT_ATTACH_SUCCESS_MSG);
          onComplete();
          break;

        case 'requires_confirmation': {
          const response = await stripe.confirmUsBankAccountSetup(clientSecret);
          const { error, setupIntent } = response;
          if (error) {
            const { message } = error || {};
            Alert.error(message || '');
            return;
          }
          setupIntent?.status === 'succeeded' && Alert.success(ACCOUNT_ATTACH_SUCCESS_MSG);
          onComplete();
          break;
        }

        case 'requires_action':
          break;

        case 'requires_payment_method':
          break;

        case 'processing':
          break;
        default:
          break;
      }
    },
    onError: (error) => {
      setFormLoading(false);
      Alert.error(error.message);
    },
  });

  const handleAddBankAccount = async () => {
    if (!stripe) {
      Alert.error(STRIPE_NOT_LOADED_TEXT);
      return;
    }
    setFormLoading(true);
    await createSetupIntent();
  };

  return (
    <Box>
      <Box {...flexCenterJustifyEnd} pt={2} columnGap={2}>
        {onCancel && (
          <Button variant="outlined" disabled={formLoading} onClick={onCancel}>
            {CANCEL_TEXT}
          </Button>
        )}
        <LoadingButton
          type="button"
          loading={formLoading}
          text={ADD_BANK_ACCOUNT_TEXT}
          loadingText="Setting up..."
          appLoader
          onClick={handleAddBankAccount}
        />
      </Box>
    </Box>
  );
};

export default AddBankAccountForm;
